import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Check, Loader2, Ticket } from "lucide-react";
import { toast } from "sonner";

/**
 * Codes d'essai gratuit (réservé à l'admin).
 *
 * Un code = une campagne, pas une personne : il porte un nombre de places, et
 * le premier arrivé prend la sienne. C'est ce qui permet d'annoncer « 15 places »
 * dans un groupe Facebook sans distribuer quinze codes à la main.
 *
 * La colonne qui compte n'est pas « places prises » mais « encore abonnés » :
 * c'est le seul chiffre qui dit si la campagne a rapporté des clients.
 */
export default function CartesCodesEssai({ className = "" }) {
  const { lang } = useI18n();
  const fr = lang !== "en";
  const [codes, setCodes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [creation, setCreation] = useState(false);
  const [copie, setCopie] = useState("");
  const [form, setForm] = useState({ places: 15, jours_validite: 60, note: "", code: "" });

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/codes-essai");
      setCodes(data || []);
    } catch {
      toast.error(fr ? "Chargement impossible" : "Could not load");
    } finally {
      setChargement(false);
    }
  }, [fr]);

  useEffect(() => { charger(); }, [charger]);

  const creer = async () => {
    setCreation(true);
    try {
      const { data } = await api.post("/admin/codes-essai", {
        places: Number(form.places) || 1,
        jours_validite: Number(form.jours_validite) || 60,
        note: form.note.trim(),
        code: form.code.trim(),
      });
      toast.success(`${fr ? "Code créé" : "Code created"} : ${data.code}`);
      setForm({ places: 15, jours_validite: 60, note: "", code: "" });
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || (fr ? "Échec" : "Failed"));
    } finally {
      setCreation(false);
    }
  };

  const basculer = async (code, actif) => {
    try {
      await api.put(`/admin/codes-essai/${code}`, { actif: !actif });
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || (fr ? "Échec" : "Failed"));
    }
  };

  const copier = (code) => {
    navigator.clipboard.writeText(code);
    setCopie(code);
    setTimeout(() => setCopie(""), 1500);
  };

  const champ = "bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500";

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-1">
        <Ticket className="h-5 w-5 text-brand-ink" />
        <h2 className="font-heading font-bold">{fr ? "Codes d'essai gratuit" : "Free trial codes"}</h2>
      </div>
      <p className="text-xs text-ink-faint mb-5 max-w-2xl">
        {fr
          ? "Un code par campagne, avec son nombre de places. Tant qu'il reste des places, le code offre le montant d'un mois du plus petit forfait — un forfait plus gros ne coûte que la différence. Une fois les places prises, le code donne 20 % de rabais à vie au lieu d'être refusé. Un compte ne peut profiter que d'UNE campagne."
          : "One code per campaign, with its own number of seats. While seats remain, the code takes off the price of one month of the smallest plan — a bigger plan costs only the difference. Once the seats are gone, the code gives 20% off for life instead of being rejected. An account can only ever use ONE campaign."}
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Places" : "Seats"}</Label>
          <Input type="number" value={form.places} onChange={(e) => setForm({ ...form, places: e.target.value })} className={champ} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Valide (jours)" : "Valid (days)"}</Label>
          <Input type="number" value={form.jours_validite} onChange={(e) => setForm({ ...form, jours_validite: e.target.value })} className={champ} />
        </div>
        <div className="col-span-2">
          <Label className="text-xs uppercase tracking-widest text-ink-soft">{fr ? "Provenance" : "Source"}</Label>
          <Input
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder={fr ? "Groupe FB Roofing Talk, appels Texas…" : "FB group Roofing Talk, Texas calls…"}
            className={champ}
          />
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="w-44">
          <Label className="text-xs uppercase tracking-widest text-ink-soft">
            {fr ? "Code imposé (option)" : "Custom code (optional)"}
          </Label>
          <Input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder={fr ? "sinon tiré au hasard" : "random if empty"}
            className={`${champ} font-mono`}
          />
        </div>
        <Button onClick={creer} disabled={creation} className="bg-brand hover:bg-brand/90 text-white rounded-md font-bold h-10">
          {creation ? <Loader2 className="h-4 w-4 animate-spin" /> : (fr ? "Créer le code" : "Create code")}
        </Button>
      </div>

      {chargement ? (
        <Loader2 className="h-5 w-5 animate-spin text-brand-ink" />
      ) : codes.length === 0 ? (
        <p className="text-sm text-ink-faint">{fr ? "Aucun code pour l'instant." : "No codes yet."}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-ink-faint border-b border-line">
                <th className="py-2 pr-4">Code</th>
                <th className="py-2 pr-4">{fr ? "Provenance" : "Source"}</th>
                <th className="py-2 pr-4">{fr ? "Forfaits offerts" : "Free plans"}</th>
                <th className="py-2 pr-4">{fr ? "Encore abonnés" : "Still subscribed"}</th>
                <th className="py-2 pr-4">{fr ? "Expire" : "Expires"}</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.code} className={`border-b border-line ${c.actif ? "" : "opacity-50"}`}>
                  <td className="py-2 pr-4 font-mono font-bold whitespace-nowrap">
                    <button onClick={() => copier(c.code)} className="inline-flex items-center gap-1.5 hover:text-brand-ink transition-colors">
                      {c.code}
                      {copie === c.code ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5 opacity-50" />}
                    </button>
                  </td>
                  <td className="py-2 pr-4 text-ink-soft">{c.note || "—"}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {c.usages} / {c.max_usages}
                    <span className="text-ink-faint"> ({c.restantes} {fr ? "libres" : "left"})</span>
                  </td>
                  <td className="py-2 pr-4 font-bold">
                    {c.actifs} <span className="font-normal text-ink-faint">/ {c.inscrits}</span>
                  </td>
                  <td className="py-2 pr-4 text-ink-faint whitespace-nowrap">
                    {c.expire_le ? String(c.expire_le).slice(0, 10) : "—"}
                  </td>
                  <td className="py-2 text-right">
                    <button onClick={() => basculer(c.code, c.actif)} className="text-xs text-ink-soft hover:text-ink underline">
                      {c.actif ? (fr ? "Fermer" : "Close") : (fr ? "Rouvrir" : "Reopen")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
